import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { UNIT_OF_MEASURE_TYPES } from 'constants/unitTypes';
import { UUID } from 'crypto';
import { BOEScenario } from 'views/DealDashboard/BOE/types';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { BudgetClassificationTypeEnum, AdjustmentType, WattsType } from './constants';

export type ProjectionType = 'linear' | 'normal' | 'gamma';

export interface IEditor {
    userId: number;
    userName: string;
    userPicture: string | null;
}

export interface CashFlowEntry {
    month: string; // e.g., "MON 20##"
    value: number; // e.g., Dollar amount for that month
}

export interface UnitMix {
    id: number | string;
    name: string; // e.g., "Studio", "1 bed / 1 bath"
    units: number;
    averageSf: number;
    pricePerSf: number;
    pricePerUnit: number;
    amountPerYear: number;
}

export interface BedMix {
    id: number | string;
    name: string;
    units: number;
    averageSf: number;
    bedsPerUnit: number;
    totalBeds: number;
    amountOfRentPerBed: number;
    amountPerYear: number;
}

interface TotalValues {
    totalPricePerSf: number;
    totalPricePerUnit: number;
    totalAmountPerYear: number;
    totalAnnualRent?: number;
    totalPricePerSfPerYear: number;
    totalPricePerSfPerMonth: number;
    totalPricePerUnitPerMonth: number;
    totalSquareFootage?: number;
    totalValuePerSfPerYear?: number;
}

export interface TotalGrossRent {
    totalUnits: number;
    rentableSf: number;
    averageSf: number;
    pricePerSf: number;
    pricePerUnit: number;
    totalAmountPerYear: number;
    totalBeds?: number;
    pricePerBed?: number;
}

export interface TotalOtherIncome {
    percentOfTotalRent: number;
    totalAmountPerYear: number;
    totalPricePerUnit?: number;
    totalPricePerBed?: number;
    totalAnnualGrowth: number;
    totalPricePerSf: number;
    totalPricePerSfPerYear: number;
    totalValuePerKwPerYear?: number;
    totalValuePerMwPerYear?: number;
    totalValuePerRackPerYear?: number;
}

export interface RentAdjustment {
    id: number;
    name: string; // e.g., "Concessions (free rent)", "Non-revenue (model) units"
    units?: number; // only used for the "Non-revenue (model) units" entry
    valuePerSf: number;
    valuePerUnit: number;
    valuePerBed?: number;
    amountPerYear: number;
}

export interface OtherIncome {
    id: number;
    name: string; // e.g., "RUBS", "Parking income", "Other income"
    annualGrowth: number;
    percentOfTotalRent: number;
    pricePerSf: number;
    pricePerSfPerYear: number;
    pricePerUnit: number;
    amountPerYear: number;
    startDate?: string | null;
    startMonth?: number | null;
    valuePerKwPerYear?: number | null;
    valuePerRackPerYear?: number | null;
    beds?: number | null;
    pricePerBed?: number | null;
}

export interface IncomeAdjustments {
    id: number;
    name: string; // e.g., "Vacancy", "Credit loss", "Adjustment"
    calculationType?: string; // e.g., "Total potential income, effective gross revenue, net rental income, net operating income"
    percentOfIncome: number;
    pricePerSf: number;
    pricePerUnit: number;
    pricePerBed?: number | null;
    amountPerYear: number;
    adjustmentType: AdjustmentType;
    pricePerSfPerMonth?: number;
    pricePerUnitPerMonth?: number;
    pricePerRackPerYear?: number | null;
    pricePerKwPerYear?: number | null;
}

export interface OperatingExpense {
    id: number;
    name: string; // e.g., "Contract services", "Insurance", "Payroll"
    annualGrowth: number;
    percentOfEffectiveGrossRevenue: number; // % of effective gross revenue
    costPerUnitPerYear: number; // e.g., $800, $1500
    valuePerBedPerYear?: number | null;
    totalAmountPerYear: number; // Total amount per year
    millRate?: number;
    isDeleteAllowed: boolean;
    operationExpenseType: string;
    valuePerSfPerYear: number;
    valuePerRackPerYear?: number | null;
    valuePerKwPerYear?: number | null;
    isRecoverable: boolean;
    recoverablePercentage: number | null;
}

interface TotalOperatingExpenses {
    totalAnnualGrowth: number;
    totalPercentOfEffectiveGrossRevenue: number;
    totalCostPerUnitPerYear: number;
    totalValuePerBedPerYear?: number | null;
    totalValuePerSfPerYear: number;
    totalAmountPerYear: number;
    totalValuePerRackPerYear?: number;
    totalValuePerKwPerYear?: number;
    totalValuePerMwPerYear?: number;
}

export interface CapitalExpenditure {
    id: number;
    name: string; // e.g., "Misc. reserves", "Other capital expenditures"
    annualGrowth: number;
    percentOfEffectiveGrossRevenue: number;
    costPerUnitPerYear: number;
    valuePerBedPerYear?: number | null;
    valuePerSfPerYear: number;
    totalAmountPerYear: number;
    isDeleteAllowed: boolean;
    operationExpenseType: string;
    valuePerRackPerYear?: number | null;
    valuePerKwPerYear?: number | null;
}

interface TotalCapitalExpenditures {
    totalPercentOfEffectiveGrossRevenue: number;
    totalCostPerUnitPerYear: number;
    totalValuePerBedPerYear?: number | null;
    totalValuePerSfPerYear: number;
    totalAmountPerYear: number;
    totalAnnualGrowth: number;
    totalValuePerRackPerYear?: number;
    totalValuePerKwPerYear?: number;
    totalValuePerMwPerYear?: number;
}

export interface ITenant {
    id: number;
    tenant: string;
    suite: string;
    leaseStart: string | null;
    rentStart: string | null;
    rentStartMonth: number | null;
    rentGrowth: number;
    rentStep: number;
    squareFootage: number;
    valuePerSfPerYear: number;
    annualRent: number;
    calculatedOn: string;
    recoveryStart: string | null;
}

export interface ITenantDataCenter {
    id: number;
    tenant: string;
    leaseStart: string | null;
    rentStart: string | null;
    rentStartMonth: number | null;
    rentGrowth: number;
    rentStep: number;
    squareFootage: number;
    racks: number | null;
    kwPerRack: number | null;
    valuePerRackPerYear: number | null;
    valuePerKwPerMonth: number | null;
    annualRent: number;
    calculatedOn: string;
    recoveryStart: string | null;
}

export interface IIncomeFloorEstimate {
    id: number;
    floor: number;
    squareFootage: number | null;
    allocationPercentage: number;
    valuePerSfPerYear: number | null;
    annualRent: number | null;
    calculatedOn: string;
}

export interface IIncomeFloorEstimateDataCenter {
    id: number;
    floor: number;
    squareFootage: number | null;
    allocationPercentage: number;
    racks: number | null;
    kwPerRack: number | null;
    valuePerRackPerYear: number | null;
    valuePerKwPerMonth: number | null;
    annualRent: number;
}

export interface IRecoveryEstimate {
    squareFootage: number;
    recoveryValuePerKwPerYear: number | null;
    recoveryValuePerSfPerYear: number | null;
    proRataShare: number;
    recoveryAmountPerYear: number | null;
}

export interface ITotalPotentialIncome {
    totalPricePerSf: number;
    totalPricePerUnit: number;
    totalPricePerBed?: number;
    totalAmountPerYear: number;
    totalPricePerSfPerYear: number;
    totalValuePerKwPerYear?: number;
    totalValuePerMwPerYear?: number;
    totalValuePerRackPerYear?: number;
}

export interface IEffectiveGrossRevenue {
    totalPricePerSf: number;
    totalPricePerUnit: number;
    totalPricePerBed?: number;
    totalAmountPerYear: number;
    totalPricePerRackPerYear?: number;
    totalPricePerKwPerYear?: number;
    totalPricePerMwPerYear?: number;
    totalPricePerFloorOrSuite?: number;
}

export interface ITotalRentalIncome {
    totalPricePerSf: number;
    totalPricePerUnit: number;
    totalPricePerBed?: number;
    totalAmountPerYear: number;
    totalAnnualRent?: number;
    totalSquareFootage?: number;
    totalValuePerSfPerYear?: number;
    totalKw?: number;
    totalRacks?: number;
    totalKwPerRack?: number;
    totalMwPerRack?: number;
    totalValuePerKwPerMonth?: number;
    totalValuePerMwPerMonth?: number;
    totalValuePerRackPerYear?: number;
}

export interface ITenantRecovery {
    id: number;
    proRataShare: number | null;
    recoveryAmountPerYear: number | null;
    recoveryStart: string | null;
    recoveryValuePerSfPerYear: number | null;
    recoveryValuePerKwPerYear?: number | null;
    startMonth: number | null;
    tenant: string;
}

export interface ITotalRecoveryIncome {
    totalAmountPerYear: number;
    totalValuePerSfPerYear: number;
    totalValuePerKwPerYear?: number;
    totalValuePerMwPerYear?: number;
}

export interface IIncomeSummary {
    unitMix: UnitMix[];
    grossRent: TotalGrossRent;
    rentAdjustments: RentAdjustment[];
    rentAdjustmentParameters: {
        id: number,
        gainLossToLeasePerYear: number,
        nonRevenueUnits: number,
    }
    totalRentalIncome: ITotalRentalIncome;
    otherIncome: OtherIncome[];
    totalOtherIncome: TotalOtherIncome;
    totalPotentialIncome: ITotalPotentialIncome;
    incomeAdjustments: IncomeAdjustments[];
    effectiveGrossRevenue: IEffectiveGrossRevenue;
    apartmentEffectiveGrossRevenue?: IEffectiveGrossRevenue;
    retailEffectiveGrossRevenue?: IEffectiveGrossRevenue;
    totalRetailIncome?: ITotalRentalIncome;
    incomeTenants?: ITenant[];
    incomeTenantsDataCenter?: ITenantDataCenter[];
    tenantRecoveries?: ITenantRecovery[];
    incomeFloorEstimates?: IIncomeFloorEstimate[];
    incomeFloorEstimatesDataCenter?: IIncomeFloorEstimateDataCenter[];
    totalRecoveryIncome?: ITotalRecoveryIncome;
    totalBeds?: number;
    bedMix?: BedMix[];
    totalUnits?: number;
    totalRentableSf?: number;
    recoveryEstimate?: IRecoveryEstimate;
    retailOtherIncome?: OtherIncome[];
    retailTotalOtherIncome?: TotalOtherIncome;
    retailIncomeAdjustments?: IncomeAdjustments[];
    retailTotalIncomeAdjustments?: TotalValues;
    retailTotalPotentialIncome?: ITotalPotentialIncome;
    incomeAdjustmentCalculationTypes: string[];
    totalRentAdjustments?: TotalValues;
    totalKw?: number;
    totalRacks?: number;
    totalMw?: number;
}

export interface ILeasingCostReserves {
    id?: number;
    newLeaseRenewalProbability?: number;
    newLeaseAverageRetailMarketRent?: number;
    newLeaseLeaseTermYears?: number;
    newLeaseTenantImprovementsPerSf?: number;
    newLeaseLeasingCommissionsPercentage?: number;
    newLeaseDowntimeFreeRentMonths?: number;
    renewalLeaseRenewalProbability?: number;
    renewalLeaseAverageRetailMarketRent?: number;
    renewalLeaseLeaseTermYears?: number;
    renewalLeaseTenantImprovementsPerSf?: number;
    renewalLeaseLeasingCommissionsPercentage?: number;
    renewalLeaseDowntimeFreeRentMonths?: number;

    // Calculated fields - New Lease
    newLeaseTenantImprovementsTotal?: number;
    newLeaseTenantImprovementsPerYear?: number;
    newLeaseLeasingCommissionsTotal?: number;
    newLeaseLeasingCommissionsPerYear?: number;
    newLeaseDowntimeFreeRentTotal?: number;
    newLeaseDowntimeFreeRentPerYear?: number;
    newLeaseTotalLeasingCosts?: number;
    newLeaseTotalLeasingCostsPerYear?: number;

    // Calculated fields - Renewal Lease
    renewalLeaseTenantImprovementsTotal?: number;
    renewalLeaseTenantImprovementsPerYear?: number;
    renewalLeaseLeasingCommissionsTotal?: number;
    renewalLeaseLeasingCommissionsPerYear?: number;
    renewalLeaseDowntimeFreeRentTotal?: number;
    renewalLeaseDowntimeFreeRentPerYear?: number;
    renewalLeaseTotalLeasingCosts?: number;
    renewalLeaseTotalLeasingCostsPerYear?: number;

    // Total calculated fields
    totalAmountPerYear?: number;
}

export interface IRecoverableExpenseUpdate {
    id: number;
    isRecoverable: boolean;
    recoverablePercentage: number;
}

export interface IExpenseSummary {
    taxPercentagePerYear: number[];
    operatingExpenses: OperatingExpense[];
    propertyTaxesOperatingExpense: OperatingExpense | null;
    totalOperatingExpenses: TotalOperatingExpenses;
    netOperatingIncome: number;
    capitalExpenditures: CapitalExpenditure[];
    leasingCostReserves?: ILeasingCostReserves;
    totalCapitalExpenditures: TotalCapitalExpenditures;
    totalCashFlowOperations: number;
}

export interface SaleProceeds {
    perUnit?: number;
    perSf?: number;
    amount?: number;
    perGrossSf?: number;
    perBed?: number;
    perRack?: number;
    perKw?: number;
    perMw?: number;
}

export interface IAssumptions {
    currentMarketCapRate: number;
    projectedCapRateAtSale: number;
    sellingCosts: number;
    saleMonth: number;
}

export interface IProjectionType {
    projectionType: ProjectionType | null;
    projectionVariable: number | null;
    curveType: string;
    img: string;
    imgPrimary: string;
}

export interface IAmountUnitTypesTotalsData {
    amount: number | null;
    costPerGrossSquareFootage: number | null;
    costPerRentableSquareFootage: number | null;
    costPerLot: number | null;
    costPerResidentialUnit: number | null;
    costPerAcre: number | null;
    costPerMegawatt: number | null;
    costPerBed: number | null;
    costPerKilowatt: number | null;
    costPerRack: number | null;
}

export interface IBudgetLinePeriod {
    periodName: string;
    periodStartDate?: string | null;
    periodEndDate?: string | null;
    projected: number;
}

export type BudgetClassificationType =
    | BudgetClassificationTypeEnum.DEFAULT
    | BudgetClassificationTypeEnum.INTEREST_RESERVE
    | BudgetClassificationTypeEnum.FINANCING_FEE
    | BudgetClassificationTypeEnum.DEVELOPER_FEE
    | BudgetClassificationTypeEnum.OPERATING_RESERVE;

export interface IProFormaUses {
    id: number;
    proFormaId: number;
    nodeId: number;
    nodeName: string;
    path: string;
    parent?: IProFormaUses | null;
    children?: IProFormaUses[] | null;
    budgetLineName?: UUID | null;
    budgetLineNickname?: string | null;
    description?: string | null;
    amount?: number | null;
    projectionType?: ProjectionType | null;
    projectionVariable?: number | null;
    percentageOfDebt?: number | null;
    percentageOfHardCosts?: number | null;
    leaseUpIncomePercentage?: number | null;
    startDate?: string | null;
    endDate?: string | null;
    costPerGrossSquareFootage?: number | null;
    costPerRentableSquareFootage?: number | null;
    costPerLot?: number | null;
    costPerResidentialUnit?: number | null;
    costPerAcre?: number | null;
    costPerMegawatt?: number | null;
    costPerBed?: number | null;
    costPerKilowatt?: number | null;
    costPerRack?: number | null;
    sourceId?: number | null;
    isDeletable?: boolean | null;
    isCalculated?: boolean | null;
    classificationType: BudgetClassificationTypeEnum;
    totals?: IAmountUnitTypesTotalsData | null;
    periods?: IBudgetLinePeriod[];
    yearlyPeriods?: IBudgetLinePeriod[];
}

export type LoanRateType = 'fixed' | 'variable';
export type DebtMetricType = DEVELOPMENT_SOURCE_METRIC;

export interface ISourceVariableLoanRatePeriodType {
    periodType: string;
    periods: {
        loanRate: string;
        periodName: string;
        periodEndDate: string;
        periodStartDate: string;
    }[];
}

export interface ISourceVariableLoanRates {
    variableLoanRates: ISourceVariableLoanRatePeriodType[];
}

export interface IProFormaSourceTableRow {
    id: number;
    proFormaId: number;
    sourceTypeId: string;
    name: string;
    traunch: number | null;
    amount: number | null;
    loanRateType: LoanRateType | null;
    fixedLoanRate: number | null;
    debtYield: number | null;
    loanToCost: number | null;
    loanToValue: number | null;
    metricType: DebtMetricType | null;
    sourcesPercentage: number | null;
    costPerGrossSquareFootage?: number | null;
    costPerRentableSquareFootage?: number | null;
    costPerLot?: number | null;
    costPerResidentialUnit?: number | null;
    costPerAcre?: number | null;
    costPerMegawatt?: number | null;
    costPerBed?: number | null;
    costPerRack?: number | null;
    costPerKilowatt?: number | null;
    periods?: ISourceProjectionPeriod[];
    yearlyPeriods?: ISourceProjectionPeriod[];
    variableLoanRates?: ISourceVariableLoanRates;
}

export interface IUnitTypeOption {
    label: string;
    value: UNIT_OF_MEASURE_TYPES;
}

export type DevelopmentUnitOfMeasureTypes =
    | UNIT_OF_MEASURE_TYPES.GROSS_SF
    | UNIT_OF_MEASURE_TYPES.RENTABLE_SF
    | UNIT_OF_MEASURE_TYPES.LOT
    | UNIT_OF_MEASURE_TYPES.RESIDENTIAL_UNIT
    | UNIT_OF_MEASURE_TYPES.ACRE
    | UNIT_OF_MEASURE_TYPES.MEGAWATT
    | UNIT_OF_MEASURE_TYPES.BED
    | UNIT_OF_MEASURE_TYPES.KILOWATT
    | UNIT_OF_MEASURE_TYPES.RACK;

export interface IUsesSelectedCategories {
    highLevels: number[];
    subcategories: number[];
    budgetClassifications: number[];
}

export interface IUsesExpandedCategories {
    highLevels: number[];
    subcategories: number[];
}

export interface IUsesActualsExpandedCategories {
    highLevels: number[];
    subcategories: number[];
}

export interface ISelectedProjection {
    name: string;
    value: number | null;
    imgPrimary: string;
}

export interface IProFormaData {
    id: number;
    dealId: number;
    name: string;
    description: string;
    dealName: string;
    address: string;
    city: string;
    county: string;
    state: string;
    zipCode: string;
    startDate: string;
    landArea: number;
    landAreaUnitTypeId: string;
    saleDate: string;
    floorAreaRatio: number;
    grossBuildableArea: number;
    buildings: number;
    averageStories: number;
    averageClearHeight: number;
    isFloorPlateAreaVaried: boolean;
    storyArea: number[];
    builtGrossSquareFootage: number;
    retailBuiltGrossSquareFootage?: number | null;
    parking: string[];
    structuredParkingSpaces?: number;
    surfaceParkingSpaces?: number;
    proFormaModelId?: number;
    subprojectId: UUID | null;
    proFormaStatusId: number;
    targetLeveredIrr: number;
    draftStep?: number;
    boeScenario: BOEScenario;
    efficiencyRatio?: number | null;
    retailEfficiencyRatio?: number | null;
    estimatedUnits?: number | null;
    estimatedRacks?: number | null;
    powerCapacityKw?: number | null;
}

export interface IMonthAnalysis {
    period: number; // e.g., 1
    periodName: string; // e.g., "MON 20##"
    leasedPercentage: string; // e.g., 0.0%
    economicOccupancyPercentage: string; // e.g., 0.0%
}

export interface IDebtService {
    percentOfStabilizedValue: number; // e.g., 49.3%
    percentOfCost: number; // e.g., 65.7%
    totalAmount: number; // e.g., $38,310,382
    annualPayment: number; // e.g., $3,454,364
    cashFlow: { value: number | null }[]; // Monthly debt service breakdown
    yearlyCashFlow: { value: number | null }[]; // Yearly debt service breakdown
}

export interface ICashFlowAfterFinancing {
    totalAnnualCashFlow: number; // e.g., $4,310,361
    cashFlow: CashFlowEntry[]; // Monthly cash flow after financing
    yearlyCashFlow: CashFlowEntry[]; // Monthly cash flow after financing
}

export enum PermanentDebtSizingMethod {
    default = 'default',
    dscr = 'dscr',
    debt_yield = 'debt_yield',
    ltv = 'ltv',
}

export interface IOperatingInfo {
    id: number;
    proFormaId?: number;
    operationStartDate: string | null; // e.g., "June 2024"
    rentalGrowthPercentage: number | null; // e.g., 3.0 (percentage)
    percentPreLeased: number | null;
    leaseUpPace: number | null;
    effectiveLeaseUpPace: number | null;
    userPaceReachesStabilization: boolean | null;
    lengthOfLease: number | null;
    initialFreeRent: number | null;
    stabilizedFreeRent: number | null;
    includeRetail: boolean | null;
    operationBeginMonth?: number;
    saleMonth?: number;
    incomeCalculationType: 'estimate' | 'tenants';
    recoveryStart: string | null;

    // income
    generalVacancy: number | null; // used in the pro forma wizard
    creditLoss: number | null; // used in the pro forma wizard
    includeGainLossToLease?: boolean | null;

    // expenses
    annualExpenseGrowthStartDate: string | null;

    // permanent debt flag (lives on operating info entity)
    includePermanentDebt: boolean | null;

    // stabilization metrics
    stabilizedMonth: number | null;
    stabilizedDate: string | null;
    occupancyUponStabilizationPercentage: number | null;
    operatingExpenseRatio: number | null;
}

export interface IPermanentDebtInfo {
    loanFundingDate: string | null;
    loanFundingMonth: number | null;
    interestOnlyPeriodMonths: number | null;
    amortizationInYears: number | null;
    annualPercentageRate: number | null;
    feesAndClosingCostsPercentage: number | null;
    feesAndClosingCostsAmount: number | null;
    permanentDebtAmount: number | null;
    permanentDebtSizingMethod: PermanentDebtSizingMethod | null;
    targetDscr: number | null;
    targetDebtYield: number | null;
    targetLtv: number | null;
    impliedDscr: number | null;
    impliedDebtYield: number | null;
    impliedLtv: number | null;
    equityDifference: number | null;
    loanFundingAmount: number | null;
    loanPayoffAmount: number | null;
    loanPayoffDate: string | null;
    loanPayoffMonth: number | null;
    debtServiceCoverageRatio: number | null;
    cashOnCashReturn: number | null;
    cashOnCashReturnMonth: number | null;
    debtService: IDebtService;
    cashFlowAfterFinancing: ICashFlowAfterFinancing;
}

/** @deprecated Use IOperatingInfo and IPermanentDebtInfo instead */
export type IOperatingAndPermanentDebtInfo = IOperatingInfo & IPermanentDebtInfo;

export interface IProFormaGeneralInfo {
    id: number;
    proFormaStatusId: number;
    proFormaModelId: number;
    proFormaModelName: string;
    dealName: string;
    dealId: number;
    boeScenarioId: number | null;
    // boeScenario optional
    name: string;
    description: string;
    address: string | null;
    city: string | null;
    county: string | null;
    state: string | null;
    zipCode: string | null;
    associatedUniversity: string | null;
    startDate: string;
    saleDate: string;
    saleMonth: number | null;
    landArea: string | null;
    landAreaUnitTypeId: number;
    floorAreaRatio: string | null;
    grossBuildableArea: string | null;
    buildings: number;
    averageStories: number;
    averageClearHeight: number | null;
    isFloorPlateAreaVaried: boolean | null;
    storyArea: number[] | null;
    builtGrossSquareFootage: string | null;
    retailBuiltGrossSquareFootage?: string | null;
    structuredParkingSpaces: number | null;
    surfaceParkingSpaces: number | null;
    undergroundParkingSpaces: number | null;
    trailerParkingSpaces: number | null;
    // grossBuildableAreaLots: IGrossBuildableAreaLotsData;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    // createdBy user schema
    // updatedBy user schema
    subprojectId: string | null; // UUID
    calculateArea?: boolean;
    areaMode?: string;
    targetLeveredIrr: number | null;
    constructionStart: string;
    constructionDuration: number;
    holdPeriod?: number | null;
    efficiencyRatio?: string | null;
    retailEfficiencyRatio?: string | null;
    estimatedUnits?: number | null;
    estimatedRacks?: number | null;
    powerCapacityKw?: number | null;
    latitude?: number | null;
    longitude?: number | null;
}

export interface IProFormaValues {
    id: number;
    address: string | null;
    city: string | null;
    county: string | null;
    state: string | null;
    zipCode: string | null;
    associatedUniversity?: string | null;
    startDate: string;
    saleDate: string;
    saleMonth: number | null;
    landArea: number | null;
    landAreaUnitTypeId: number;
    floorAreaRatio: number | null;
    grossBuildableArea: number | null;
    buildings: number;
    averageStories: number;
    averageClearHeight: number | null;
    isFloorPlateAreaVaried: boolean | null;
    storyArea: number[] | null;
    builtGrossSquareFootage: number | null;
    retailBuiltGrossSquareFootage?: number | null;
    structuredParkingSpaces: number | null;
    surfaceParkingSpaces: number | null;
    undergroundParkingSpaces: number | null;
    trailerParkingSpaces: number | null;
    dealName?: string;
    calculateArea?: boolean;
    areaMode?: string;
    constructionStart?: string | null;
    constructionDuration?: number | null;
    holdPeriod?: number | null;
    buildableLots?: IGrossBuildableAreaLotsData;
    efficiencyRatio?: number | null;
    retailEfficiencyRatio?: number | null;
    estimatedUnits?: number | null;
    estimatedRacks?: number | null;
    powerCapacityKw?: number | null;
}

export interface IDealUnitType {
    id: number;
    dealId: number;
    unitType: number;
    value: number;
}

export interface IProFormaRoomDeal {
    id: number;
    dealName: string;
    dealUnitTypes: IDealUnitType[];
}

export interface IUsesSummary {
    developmentStartDate?: string;
    developmentEndDate?: string;
    developmentStartMonth: number | null;
    developmentEndMonth: number | null;
    uses: IProFormaUses[];
    totals: IAmountUnitTypesTotalsData;
    periodsTotals?: IBudgetLinePeriod[];
    yearlyPeriodsTotals?: IBudgetLinePeriod[];
}

export interface ISourceProjectionPeriod {
    periodName: string;
    amountToFund: number;
    periodStartDate?: string | null;
    periodEndDate?: string | null;
    proRata?: number | null;
}

export interface ISourcesSummary {
    sources: IProFormaSourceTableRow[];
    totals: IAmountUnitTypesTotalsData;
    periodsTotals?: ISourceProjectionPeriod[];
    yearlyPeriodsTotals?: ISourceProjectionPeriod[];
}

export interface ICashFlowEntityNameValue {
    name: string;
    value: number;
}

export interface IOperatingPeriodCashFlows {
    month?: number | null;
    operatingMonth?: number | null;
    operatingYear?: number | null;

    // operating info
    leasedPercentage?: string | null;
    economicOccupancyPercentage?: string | null;

    // income
    units: ICashFlowEntityNameValue[];
    beds?: ICashFlowEntityNameValue[];
    grossRent?: number | null;
    rentAdjustments?: ICashFlowEntityNameValue[];
    totalRentalIncome?: number | null;
    totalRetailIncome?: number | null;
    otherIncome: ICashFlowEntityNameValue[];
    totalOtherIncome?: number | null;
    totalPotentialIncome?: number | null;
    incomeAdjustments?: ICashFlowEntityNameValue[];
    effectiveGrossRevenue?: number | null;
    apartmentEffectiveGrossRevenue?: number | null;
    retailEffectiveGrossRevenue?: number | null;
    incomeTenants?: ICashFlowEntityNameValue[];
    incomeTenantsDataCenter?: ICashFlowEntityNameValue[];
    totalFloorEstimatesIncome?: string | null;
    tenantRecoveries?: ICashFlowEntityNameValue[];
    incomeFloorEstimates?: { floor: number, value: number | null, concessions: number | null, concessionPercentage: number | null }[];
    recoveryEstimate?: number | null;
    totalRecoveryIncome?: number | null;
    totalIncomeAdjustments?: number | null;
    retailOtherIncome?: ICashFlowEntityNameValue[];
    retailTotalOtherIncome?: number | null;
    retailIncomeAdjustments?: ICashFlowEntityNameValue[];
    retailTotalIncomeAdjustments?: number | null;
    retailTotalPotentialIncome?: number | null;

    concessions?: number | null;
    nonRevenueUnits?: number | null;
    generalVacancy?: string | null;
    creditLoss?: string | null;
    gainLossToLease?: string | null;

    // expenses
    operatingExpenses: ICashFlowEntityNameValue[];
    propertyTaxesOperatingExpense?: { value: number | null };
    totalOperatingExpenses?: number | null;
    capitalExpenditures: ICashFlowEntityNameValue[];
    netOperatingIncome?: string | null;
    miscellaneousCapitalReserves?: string | null;
    otherCapitalExpenditures?: ICashFlowEntityNameValue[];
    totalCapitalExpenditures?: number | null;

    // cash flow
    cashFlowFromOperations?: string | null;
}

export interface ISourceType {
    id: number;
    name: string;
    correlationId: string;
    sourceTypeCategory: string;
}

export interface IProjectSourcesActuals {
    monthly: IProjectSourcesPeriod[];
    yearly: IProjectSourcesPeriod[];
}

export interface IProjectActuals {
    uses: IProjectUsesActuals;
    sources?: IProjectSourcesActuals;
    discrepancies: string[];
}

export interface IProFormaSocketRoomData {
    deal: IProFormaRoomDeal;
    generalInfo: IProFormaGeneralInfo;
    buildableLots: IGrossBuildableAreaLotsData;
    usesSummary: IUsesSummary;
    sourcesSummary: ISourcesSummary;
    cashFlowPeriods?: string[];
    yearlyCashFlowPeriods?: string[];

    // Operations tab
    operatingInfo: IOperatingInfo;
    permanentDebtInfo: IPermanentDebtInfo;
    operatingPeriodCashFlows?: (IOperatingPeriodCashFlows | null)[];
    yearlyOperatingPeriodCashFlow?: (IOperatingPeriodCashFlows | null)[];
    incomeSummary: IIncomeSummary;
    expenseSummaryInputs?: IExpenseSummary;
    expenseSummary: IExpenseSummary;

    // Disposition tab
    disposition?: IDispositionData;

    returns: IProFormaReturnsData;
    waterfallAnalysis: IWaterfallAnalysis;
    sourceTypes: ISourceType[];

    // project actuals
    projectActuals: IProjectActuals;
}

export interface ICashFlowPeriod {
    analysisDate: string;
    cashFlowFromOperations: number;
    constructionLoanDraws: number;
    constructionLoanInterest: number;
    constructionLoanPayoff: number;
    developmentUsesPeriodTotals: {
        periodEndDate: string | null;
        periodName: string;
        periodStartDate: string | null;
    };
    equityProceedFromCapitalEvents: number;
    netLeveredCashFlow: number;
    netSaleProceeds: number;
    netUnleveredCashFlow: number;
    permanentLoanDebtService: number;
    permanentLoanClosingCosts: number;
    permanentLoanFunding: number;
    projectCost: number;
    financingFee: number;
    permanentLoanPayoff: number;
}

export interface IProFormaReturnsData {
    breakevenOccupancy: number;
    cashFlow: ICashFlowPeriod[];
    yearlyCashFlow: ICashFlowPeriod[];
    totalProjectCost: string;
    totalLoanInterest: string;
    totalFinancingFees: string;
    totalConstructionLoanDraws: string;
    totalCashFlowFromOperations: string;
    totalConstructionLoanPayoff: string;
    totalEquityProceedFromCapitalEvents: string;
    totalPermanentLoanFunding: string;
    totalPermanentLoanClosingCosts: string;
    totalPermanentLoanDebtService: string;
    totalPermanentLoanPayoff: string;
    netUnleveredEquityMultiple: string;
    netLeveredIrr: number;
    unleveredRoi: number;
    netLeveredEquityMultiple: number;
    yieldOnCost: IProFormaTrendBreakdown;
    totalNetSaleProceeds: number;
    netUnleveredNetProfit: string;
    netLeveredNetProfit: string;
    totalNetUnleveredCashFlow: string;
    totalNetLeveredCashFlow: string;
    netUnleveredCashFlow: string;
    netUnleveredIrr: number;
    netLeveredCashFlow: string;
    developmentSpread: IProFormaTrendBreakdown;
    marketCapitalizationRate: {
        current: number;
        onSale: number;
        trended: number;
    };
    leveredRoi: number;
    projectProfit?: number | null;
    projectProfitMargin?: number | null;
}

export interface IProFormaTrendBreakdown {
    untrended: number;
    trended: number;
    sale: number;
}

export interface IUpdateProFormaKeyValues {
    key: string;
    value: string | number | boolean | number[] | null;
}

export interface ICreateBudgetClassificationPostBody {
    budgetLineNickname?: string;
    description?: string;
    parentId: number;
}

export interface IBudgetClassificationPatchData {
    id: number;
    description?: string;
    budgetLineNickname?: string;
    amount?: string;
    projectionType?: ProjectionType | null;
    projectionVariable?: number | null;
    startDate?: string;
    endDate?: string;
    percentageOfDebt?: string | null;
    percentageOfHardCosts?: string | null;
    leaseUpIncomePercentage?: string | null;
    costPerGrossSquareFootage?: string;
    costPerRentableSquareFootage?: string;
    calculatedOn?: BudgetLineCalculatedOn | null;
}

export interface ICreateProFormaSourceRequestData {
    sourceTypeId: string;
    name: string;
    traunch: number;
    amount: string;
    calculatedOn?: SourceCalculatedOn | null;
}

export interface IGrossBuildableAreaLot {
    id: number;
    landArea: number;
    landAreaUnitTypeId: number;
    floorAreaRatio: number;
    grossBuildableArea: number;
    areaMode: string;
}

export interface IGrossBuildableAreaLotsData {
    totalGrossBuildableArea: number;
    totalLandAreaSquareFootage: number;
    totalLandAreaAcres?: number;
    totalFar: number;
    grossBuildableAreaLots: IGrossBuildableAreaLot[];
}

export interface ISourcePatchData {
    name?: string;
    traunch?: number;
    amount?: number;
    loanRateType?: LoanRateType;
    fixedLoanRate?: number | null;
    debtYield?: number;
    loanToCost?: number;
    loanToValue?: number;
    variableLoanRates?: ISourceVariableLoanRates;
    calculatedOn?: SourceCalculatedOn | null;
}

export interface IDebtMetricOption {
    label: string;
    value: DEVELOPMENT_SOURCE_METRIC;
}

export type UsesView = 'cards' | 'table';

export interface IProFormaLocalStorage {
    selectedScenario: number;
    showProjectActuals: boolean;
    selectedPeriodCadence: string;
    hideWarnings: boolean;
    showCents: boolean;
    wattsType: WattsType;
    developmentUnitType: UNIT_OF_MEASURE_TYPES;
}

export interface IProFormaPageLocalStorage {
    [proFormaId: number]: IProFormaLocalStorage;
}

export interface CashFlowMetrics {
    untrended: number;
    trended: number;
    sale: number;
}

export interface ReturnsMetrics {
    roi: number;
    emx: number;
    irr: number;
}

export interface PropertyReturns {
    levered: ReturnsMetrics;
    unlevered: ReturnsMetrics;
}

export interface PartnershipReturns {
    limitedPartner: ReturnsMetrics;
    generalPartner: ReturnsMetrics;
}

export interface DevelopmentReturns {
    yieldOnCost: CashFlowMetrics;
    marketCapRate: CashFlowMetrics;
    developmentSpread: CashFlowMetrics;
    projectProfit?: number | null;
    projectProfitMargin?: number | null;
}

export interface Returns {
    property: PropertyReturns;
    partnership: PartnershipReturns;
    development: DevelopmentReturns;
}

export interface CashFlows {
    effectiveGrossRevenue: CashFlowMetrics;
    operatingExpenses: CashFlowMetrics;
    netOperatingIncome: CashFlowMetrics;
    capitalExpenditures: CashFlowMetrics;
    cashFlowOperations: CashFlowMetrics;
    capRate: CashFlowMetrics;
    valuation: CashFlowMetrics;
    totalProjectCost: CashFlowMetrics;
    valuationPerUnit: CashFlowMetrics;
    valuationPerSf: CashFlowMetrics;
}

export interface DevelopmentCost {
    percentage: string;
    perBuiltSf: string;
    perUnit: string;
    amount: string;
}

export interface Development {
    landCosts: DevelopmentCost;
    hardCosts: DevelopmentCost;
    softCosts: DevelopmentCost;
    carryCosts: DevelopmentCost;
    totalUses: DevelopmentCost;
    constructionLoan: DevelopmentCost;
    mezzanineLoan: DevelopmentCost;
    limitedPartner: DevelopmentCost;
    generalPartner: DevelopmentCost;
    totalSources: DevelopmentCost;
}

export interface SummaryDetails {
    proFormaModelId: number;
    address: string | null;
    fullAddress: string | null;
    units: number;
    netRentableArea: number;
    landArea: number;
    buildings: number;
    stories: number;
    parkingSpaces: number;
    parkingRatios: number;
    description: string;
    longitude: number;
    latitude: number;
    city: string | null;
    state: string | null;
    zipCode: string | null;
}

export interface GrowthRate {
    rentalIncome: number;
    expenses: number;
}

export interface EffectiveRent {
    perSf: number;
    perUnit: number;
}

export interface SummaryQuickStats {
    growthRate: GrowthRate;
    effectiveRent: EffectiveRent;
    generalVacancy: number;
    creditLoss: number;
    operatingExpenseRatio: number;
    breakevenOccupancy: number;
    dispositionNetProceeds: number;
    loanPayoff: number;
    equityProceedsFromSale: number;
    cashOnCashReturn: number;
}

export interface SummaryGeneralInfo {
    dealName: string;
}

export interface TimelineData {
    analysisStart?: string;
    firstUnitDelivered?: string;
    developmentStartDate?: string;
    developmentEndDate?: string;
    operationStartDate?: string;
    firstStabilizedMonth?: string;
    saleMonth?: string;
    dealName?: string;
}

export interface ProFormaExecutiveSummary {
    general: SummaryGeneralInfo;
    details: SummaryDetails;
    keyStats: SummaryQuickStats;
    cashFlows: CashFlows;
    returns: Returns;
    development: Development;
    timelineData: TimelineData;
}

export interface IPatchOperatingInfoData {
    operationStartDate?: string; // Date when operations start
    rentalGrowthPercentage?: number; // Rental growth percentage, saved as decimal so 2 percent is 0.02 in the db
    percentPreLeased?: number; // Pre-leased percentage, saved as decimal so 2 percent is 0.02 in the db
    leaseUpPace?: number; // Lease up pace: number of units per month
    lengthOfLease?: number; // Length of lease, number of months entry
    initialFreeRent?: number; // Initial free rent, number of months entry
    stabilizedFreeRent?: number; // Stabilized free rent, number of months entry
    generalVacancy?: number; // General vacancy percentage, saved as decimal so 2 percent is 0.02 in the db
    creditLoss?: number; // Credit loss percentage, saved as decimal so 2 percent is 0.02 in the db
    includeRetail?: boolean; // Boolean, whether user has retail space or not
    includeGainLossToLease?: boolean; // Boolean, whether user wants to include gain/loss to lease
    occupancyUponStabilizationPercentage?: number; // Occupancy upon stabilization percentage, saved as decimal so 2 percent is 0.02 in the db
    permanentDebtAmount?: number; // Permanent debt amount, decimal
    annualExpenseGrowthStartDate?: Date; // Date of Expenses section start date
    taxPercentagePerYear?: number[]; // Array of tax percentages per year
    incomeCalculationType?: string; // Income calculation type for non-apartment models, either "estimate" or "tenants"
    recoveryStart?: string;
    permanentDebt?: {
        includePermanentDebt?: boolean;
        loanFundingDate?: string;
        interestOnlyPeriodMonths?: number | null;
        amortizationInYears?: number;
        annualPercentageRate?: number;
        feesAndClosingCostsPercentage?: number;
        permanentDebtSizingMethod?: PermanentDebtSizingMethod;
        targetDscr?: number;
        targetDebtYield?: number;
        targetLtv?: number;
    };
}

export interface DispositionItem {
    id: number | string;
    name?: string;
    untrended?: number;
    trended?: number;
    sale?: number;
}

export interface DispositionTotal {
    untrended?: number;
    trended?: number;
    sale?: number;
}

export interface MillRates {
    untrended?: number;
    trended?: number;
    sale?: number;
}

export interface IDispositionData {
    proFormaSaleDate?: string;
    saleMonth: number | null;
    sellingCostsPercent: number;
    marketCapitalizationRate: {
        current: number;
        onSale: number;
        trended: number;
    };
    trendedPeriod?: {
        start: number;
        end: number;
    };
    salePeriod?: {
        start: number;
        end: number;
    };
    cashFlowFromOperations?: DispositionTotal;
    equityProceedsFromSale?: SaleProceeds;
    grossRent?: DispositionTotal;
    grossRentAdjustments?: DispositionTotal;
    grossSaleProceeds?: SaleProceeds;
    sellingCosts?: SaleProceeds;
    loanPayoff?: SaleProceeds;
    netOperatingIncome?: DispositionTotal;
    netSaleProceeds?: SaleProceeds;
    stabilizedValue?: DispositionTotal;
    stabilizedValuePerSf?: DispositionTotal;
    stabilizedValuePerUnit?: DispositionTotal;
    stabilizedValuePerBed?: DispositionTotal;
    stabilizedValuePerGrossSf?: DispositionTotal;
    stabilizedValuePerRack?: DispositionTotal;
    stabilizedValuePerMw?: DispositionTotal;
    stabilizedValuePerKw?: DispositionTotal;
    totalCapitalExpenditures?: DispositionTotal;
    totalEffectiveGrossRevenue?: DispositionTotal;
    totalIncomeAdjustments?: DispositionTotal;
    totalOperatingExpenses?: DispositionTotal;
    totalOtherIncome?: DispositionTotal;
    totalPotentialIncome?: DispositionTotal;
    totalRentalIncome?: DispositionTotal;
    totalRecoveryIncome?: DispositionTotal;
    percentOfTotalProjectCost?: DispositionTotal;
}

export type SensitivityAnalysisParameter = 'Gross Rent vs Vacancy Rate' |
    'Exit Capitalization Rate vs Gross Rent' |
    'Lease Up Pace vs Gross Rent' |
    'Operating Expenses vs Gross Rent' |
    'Exit Capitalization Rate vs Hard and Soft Costs' |
    'Interest Rate vs Loan to Cost' |
    'Acquisition Price vs Exit Capitalization Rate';

export type BudgetLineCalculatedOn =
    | 'amount'
    | 'percentage_of_debt'
    | 'percentage_of_hard_costs'
    | 'cost_per_gross_square_footage'
    | 'cost_per_rentable_square_footage';

export type SourceCalculatedOn = 'amount' | 'metric_percentage';

export interface AnalysisBySecondParameter {
    secondParameterValue: string | null;
    exitValuation?: string | null;
    leveredIrr?: string | null;
    leveredEmx?: string | null;
    netOperatingIncome?: string | null;
    constructionDebtAmount?: string | null;
    stabilizedYieldOnCost?: string | null;
    netProfit?: string | null;
    status?: string;
}

export interface AnalysisByFirstParameter {
    firstParameterValue: string;
    bySecond: AnalysisBySecondParameter[];
}

export interface SensitivityAnalysisResponse {
    proFormaId: number;
    firstParameter: string;
    firstParameterStepMultiplier: number;
    secondParameter: string;
    secondParameterStepMultiplier: number;
    sensitivityAnalysis: AnalysisByFirstParameter[];
}

export interface SensitivityAnalysisParams {
    parameters: SensitivityAnalysisParameter;
    firstParameterStepMultiplier: number;
    secondParameterStepMultiplier: number;
}

export interface IPeriodData {
    periodName: string;
    projected: number;
    spending?: number;
    funding: number;
    inCurrentPeriod: boolean;
}

export interface IActualsScenario {
    id: number;
    name: string;
    value: number;
}

export interface ICashFlowBudgetClassification {
    id: number;
    nodeName: string;
    description: string;
    totalSpentToDate: number;
    balanceRemaining: number;
    balanceRemainingAnticipated: number;
    projected: number;
    projectionType: string;
    projectionVariable?: number | null;
    projectionStartDate?: string;
    projectionEndDate?: string;
    startDate?: string;
    endDate?: string;
    totalAnticipatedCost?: number;
    totalRevisedBudget?: number;
    periodData?: IPeriodData[];
    yearlyPeriodData?: IPeriodData[];
    scenarios?: IActualsScenario[];
    children?: ICashFlowBudgetClassification[];
}

export interface IProjectUsesActuals {
    subprojectId: number;
    subprojectUuid: string;
    subprojectStartDate: string;
    subprojectEndDate: string;
    subprojectName: string;
    developmentSiteName: string;
    isEarlyPlanning: boolean;
    periodType: string;
    forecastMetric: string;
    budgetClassifications?: ICashFlowBudgetClassification[];
}

export interface IProjectSourceDetail {
    id: number;
    name: string;
    sourceTypeName: string;
    amount: number;
    amountFunded: number;
    amountRemaining: number;
    balanceRemaining: number;
    proRataSourceShareOfTraunch: number;
    amountRemainingProjected?: number;
    adjustedProRataSourceShareOfTraunch?: number;
    amountToFund?: number;
}

export interface IProjectTraunchCashFlow {
    traunch: number;
    amount: number;
    amountFunded: number;
    amountRemaining: number;
    amountRemainingProjected?: number;
    amountToFund?: number;
    sources: IProjectSourceDetail[];
}

export interface IProjectSourcesPeriod {
    funding: number;
    inCurrentPeriod: boolean;
    periodStartDate: string;
    periodEndDate: string;
    periodName: string;
    projected: number;
    totalAmountFunded: number;
    totalAmountToFund: number;
    totalSourceAmount: number;
    totalSourceAmountRemaining: number;
    totalSourceAmountRemainingProjected: number;
    sources: IProjectTraunchCashFlow[];
}

export interface ISubproject {
    id: number;
    subprojectName: string;
    developmentProject: {
        projectName: string;
    };
    budgetScenarios: Array<{
        id: number;
        name: string;
        statusId: number;
        correlationId: string;
    }>;
    correlationId: string;
    earlyPlanning: boolean;
}

export interface OrganizationSubproject {
    subprojectId: number;
    organizationId: number;
    subproject: ISubproject;
}

export interface IWaterfallAnalysis {
    developerFee?: number;
    aumFeeType?: AUMFeeType;
    aumFeePercentage?: string;
    aumFeeStartDate?: string;
    includeDeveloperFee?: boolean;
    aumFeeStartDateMonthIdx?: number;
    aumFee?: string;
    isSingleGp?: boolean;
    isSingleLp?: boolean;
    gp?: IProFormaPartner;
    lp?: IProFormaPartner;
    waterfallSummary?: IWaterfallSummary;
    contributions?: IContributions;
    contributionsCashFlow: IContributionsCashFlow[];
    distributionWaterfall?: IDistributionWaterfall;
    distributionWaterfallCashFlow: IDistributionWaterfallCashFlow[];
    distributions?: IDistributions;
    distributionsCashFlow: IDistributionsCashFlow[];
    netCashFlow?: INetCashFlow;
    netCashFlowCashFlow: INetCashFlowCashFlow[];
    gpGpContributions?: IGpGpContributions;
    gpGpContributionsCashFlow: IGpGpContributionsCashFlow[];
    gpGpDistributionWaterfall?: IGpGpDistributionWaterfall;
    gpGpDistributionWaterfallCashFlow: IGpGpDistributionWaterfallCashFlow[];
    gpGpDistributions?: IGpGpDistributions;
    gpGpDistributionsCashFlow: IGpGpDistributionsCashFlow[];
    gpGpNetCashFlow?: IGpGpNetCashFlow;
    gpGpNetCashFlowCashFlow: IGpGpNetCashFlowCashFlow[];

    partnershipWaterfall?: IPartnershipWaterfall;

    yearlyContributionsCashFlow: IContributionsCashFlow[];
    yearlyDistributionWaterfallCashFlow: IDistributionWaterfallCashFlow[];
    yearlyDistributionsCashFlow: IDistributionsCashFlow[];
    yearlyNetCashFlowCashFlow: INetCashFlowCashFlow[];
    yearlyGpGpContributionsCashFlow: IGpGpContributionsCashFlow[];
    yearlyGpGpDistributionWaterfallCashFlow: IGpGpDistributionWaterfallCashFlow[];
    yearlyGpGpDistributionsCashFlow: IGpGpDistributionsCashFlow[];
    yearlyGpGpNetCashFlowCashFlow: IGpGpNetCashFlowCashFlow[];
}

export interface AUMFeeType {
    type: 'development_cost_percentage' | 'equity_percentage';
}
export interface IProFormaPartner {
    id?: number;
    name?: string;
    contributionShare?: string;
    preferredReturnDistributionPercentage?: string;
    children?: IProFormaPartner[];
}

export interface IWaterfallSummary {
    lpName?: string;
    gp1Name?: string;
    gp2Name?: string;

    lpContributions?: string;
    gp1Contributions?: string;
    gp2Contributions?: string;

    lpNetProfit?: string;
    gp1NetProfit?: string;
    gp2NetProfit?: string;

    lpRoi?: string;
    gp1Roi?: string;
    gp2Roi?: string;

    lpEmx?: string;
    gp1Emx?: string;
    gp2Emx?: string;

    lpIrr?: string;
    gp1Irr?: string;
    gp2Irr?: string;
}

export interface IContributions {
    gpSharePercent?: string;
    lpSharePercent?: string;
    totalSharePercent?: string;
    gpAmount?: string;
    lpAmount?: string;
    totalAmount?: string;
}
export interface IContributionsCashFlow {
    gpAmount?: string;
    lpAmount?: string;
    totalAmount?: string;
}

export interface IDistributionWaterfall {
    type: string;
    hurdleMetricType: string;
    preferredReturnHurdleRate?: string;
    secondTierHurdleRate?: string;
    thirdTierHurdleRate?: string;
    fourthTierHurdleRate?: string;
    prefReturnGpPromote?: string;
    secondTierGpPromote?: string;
    thirdTierGpPromote?: string;
    fourthTierGpPromote?: string;
    prefReturnDistributionPercentToGp?: string;
    tier2DistributionPercentToGp?: string;
    tier3DistributionPercentToGp?: string;
    tier4DistributionPercentToGp?: string;
    gpCatchUp?: number;

    prefReturnDistributionPercentToLp?: string;
    tier2DistributionPercentToLp?: string;
    tier3DistributionPercentToLp?: string;
    tier4DistributionPercentToLp?: string;
}

export interface IDistributionWaterfallCashFlow {
    prefReturnAmount?: string;
    tier2Amount?: string;
    tier3Amount?: string;
    tier4Amount?: string;
}

export interface IDistributions {
    gpName?: string;
    lpName?: string;
    gpAmount?: string;
    lpAmount?: string;
    totalAmount?: string;
    gpShare?: string;
    lpShare?: string;
    totalShare?: string;
}

export interface IDistributionsCashFlow {
    gpAmount?: string;
    lpAmount?: string;
    totalAmount?: string;
}

export interface INetCashFlow {
    gpName?: string;
    lpName?: string;
    gpAmount?: string;
    lpAmount?: string;
    totalAmount?: string;
    gpShare?: string;
    lpShare?: string;
    totalShare?: string;
    gpEmx?: string;
    lpEmx?: string;
    totalEmx?: string;
    gpIrr?: string;
    lpIrr?: string;
    totalIrr?: string;
}

export interface INetCashFlowCashFlow {
    gpAmount?: string;
    lpAmount?: string;
    totalAmount?: string;
}

export interface IGpGpContributions {
    gp1ShareOfEquity?: string;
    gp2ShareOfEquity?: string;
    totalShareOfEquity?: string;

    gp1ShareOfDevFee?: string;
    gp2ShareOfDevFee?: string;
    totalShareOfDevFee?: string;

    gp1EquityAmount?: string;
    gp2EquityAmount?: string;
    totalEquityAmount?: string;
}

export interface IGpGpContributionsCashFlow {
    gp1Amount?: string;
    gp2Amount?: string;
    totalAmount?: string;
}

export interface IGpGpDistributionWaterfall {
    type: string;
    hurdleMetricType: string;
    preferredReturnHurdleRate?: string;
    secondTierHurdleRate?: string;
    thirdTierHurdleRate?: string;
    fourthTierHurdleRate?: string;

    prefReturnGpPromote?: string;
    secondTierGpPromote?: string;
    thirdTierGpPromote?: string;
    fourthTierGpPromote?: string;

    prefReturnDistributionPercentToGp1?: string;
    tier2DistributionPercentToGp1?: string;
    tier3DistributionPercentToGp1?: string;
    tier4DistributionPercentToGp1?: string;

    prefReturnDistributionPercentToGp2?: string;
    tier2DistributionPercentToGp2?: string;
    tier3DistributionPercentToGp2?: string;
    tier4DistributionPercentToGp2?: string;
}

export interface IGpGpDistributionWaterfallCashFlow {
    prefReturnAmount?: string;
    tier2Amount?: string;
    tier3Amount?: string;
    tier4Amount?: string;
}

export interface IGpGpDistributions {
    gp1Name?: string;
    gp2Name?: string;
    gp1Amount?: string;
    gp2Amount?: string;
    totalAmount?: string;
    gp1Share?: string;
    gp2Share?: string;
    totalShare?: string;
}

export interface IGpGpDistributionsCashFlow {
    gp1Amount?: string;
    gp2Amount?: string;
    totalAmount?: string;
}

export interface IGpGpNetCashFlow {
    gp1Name?: string;
    gp2Name?: string;
    gp1Amount?: string;
    gp2Amount?: string;
    totalAmount?: string;
    gp1Share?: string;
    gp2Share?: string;
    totalShare?: string;
    gp1Emx?: string;
    gp2Emx?: string;
    totalEmx?: string;
    gp1Irr?: string;
    gp2Irr?: string;
    totalIrr?: string;
}

export interface IGpGpNetCashFlowCashFlow {
    gp1Amount?: string;
    gp2Amount?: string;
    totalAmount?: string;
}

interface IReturnOfCapitalAndHurdle1Distribution {
    assetManagementFee?: string;
    adjustedNetLeveredCashFlow?: string;
    beginningBalanceLpCapitalAccount?: string;
    requiredReturnByLpPref?: string;
    contributionsFromLp?: string;
    distributionsToLp?: string;
    endingBalanceLpCapitalAccount?: string;
    beginningBalanceGpCapitalAccount?: string;
    requiredReturnByGpPref?: string;
    contributionsFromGp?: string;
    distributionsToGp?: string;
    endingBalanceGpCapitalAccount?: string;
    irrCheck?: string;
    developerFeeToGpPlusAumFee?: string;
    totalDistributions?: string;
    cashFlowRemaining?: string;
}

interface IHurdle2Distribution {
    beginningBalanceLpCapitalAccount?: string;
    requiredReturnByLpPref?: string;
    priorDistributions?: string;
    distributionsToLp?: string;
    endingBalanceLpCapitalAccount?: string;
    distributionsToGp?: string;
    totalDistributions?: string;
    cashFlowRemaining?: string;
}

interface IHurdle3Distribution {
    beginningBalanceLpCapitalAccount?: string;
    requiredReturnByLpPref?: string;
    priorDistributions?: string;
    distributionsToLp?: string;
    endingBalanceLpCapitalAccount?: string;
    distributionsToGp?: string;
    totalDistributions?: string;
    cashFlowRemaining?: string;
}

interface IHurdle4Distribution {
    distributionsToLp?: string;
    distributionsToGp?: string;
    totalDistributions?: string;
}

interface IGpLpDistribution {
    hurdle1DistributionCatchUp?: boolean;
    returnOfCapitalAndHurdle1Distribution: IReturnOfCapitalAndHurdle1Distribution[];
    hurdle2Distribution: IHurdle2Distribution[];
    hurdle3Distribution: IHurdle3Distribution[];
    hurdle4Distribution: IHurdle4Distribution[];
}

interface IGpGpReturnOfCapitalAndHurdle1Distribution {
    netLeveredGpCashFlowBeforeDevFee?: string;
    beginningBalanceGp2CapitalAccount?: string;
    requiredReturnByGp2Pref?: string;
    contributionsFromGp2?: string;
    distributionsToGp2?: string;
    endingBalanceGp2CapitalAccount?: string;
    distributionsToGp1?: string;
    totalDistributions?: string;
    cashFlowRemaining?: string;
}

interface IGpGpHurdle2Distribution {
    beginningBalanceGp2CapitalAccount?: string;
    requiredReturnByGp2Pref?: string;
    priorDistributions?: string;
    distributionsToGp2?: string;
    endingBalanceGp2CapitalAccount?: string;
    distributionsToGp1?: string;
    totalDistributions?: string;
    cashFlowRemaining?: string;
}

interface IGpGpHurdle3Distribution {
    beginningBalanceGp2CapitalAccount?: string;
    requiredReturnByGp2Pref?: string;
    priorDistributions?: string;
    distributionsToGp2?: string;
    endingBalanceGp2CapitalAccount?: string;
    distributionsToGp1?: string;
    totalDistributions?: string;
    cashFlowRemaining?: string;
}

interface IGpGpHurdle4Distribution {
    distributionsToGp2?: string;
    distributionsToGp1?: string;
    totalDistributions?: string;
}

interface IGpGpDistribution {
    hurdle1DistributionCatchUp?: boolean;
    returnOfCapitalAndHurdle1Distribution: IGpGpReturnOfCapitalAndHurdle1Distribution[];
    hurdle2Distribution: IGpGpHurdle2Distribution[];
    hurdle3Distribution: IGpGpHurdle3Distribution[];
    hurdle4Distribution: IGpGpHurdle4Distribution[];
}

export interface IPartnershipWaterfall {
    gpLpDistribution: IGpLpDistribution;
    gpGpDistribution?: IGpGpDistribution;
}

export type HurdleMetricType = 'irr' | 'cash_on_cash' | 'equity_multiple';

export interface IUpdateDistributionWaterfallData {
    hurdleMetricType?: HurdleMetricType;
    preferredReturnHurdleRate?: number;
    secondTierHurdleRate?: number;
    secondTierGpPromote?: number;
    thirdTierHurdleRate?: number;
    thirdTierGpPromote?: number;
    fourthTierGpPromote?: number;
    gpCatchUp?: number;
}

export interface IUpdateDistributionWaterfallRequest {
    room: string;
    proFormaId: number;
    waterfallType: IWaterfallType;
    data: IUpdateDistributionWaterfallData;
}

export type IWaterfallType = 'basic' | 'multi_gp';

export type IPeriod = {
    periodName: string;
    periodDate: string;
    values: Record<string, string | null>;
}

export type INameValue = {
    name: string;
    value: number;
}

export interface IVariableLoanRatesByPeriodName {
    [key: string]: {
        value: string;
        cadenceIndex: number;
        periodIndex: number;
    };
}

export interface IPeriodOptionValue<T = any> {
    label: string;
    value: T;
    isPlaceholder?: boolean;
}

export interface IFormattedCashFlowPeriod {
    label: string;
    value: string;
    period: string;
    isPlaceholder?: boolean;
}

export interface BOEProFormaDiscrepancies {
    [key: string | number]: string[];
}

export interface IKeyStatsCard {
    icon: IconDefinition;
    title: string;
    formatter: (value: number | null) => string;
    value: number | null;
    comparisonValue: number;
    marginClass?: string;
}

export interface ISideNavFooterKeyStatDiscrepancy {
    cardColor: string;
    iconColor: string;
}

export interface IImportBudgetClassificationFromProjectData {
    scenarioId: number;
    subprojectId: number;
    shouldImportBudgetLines?: boolean;
    budgetMultiplierPercentage?: number;
    shouldImportCurveFits?: boolean;
}

export type UsesCardEditVariant = 'dollar-only' | 'dollar-or-pct-of-hard' | 'dollar-or-pct-of-debt';

export interface UsesCardEditContext {
    hardTotal?: number;
    debtAmount?: number;
    initialPercent?: number;
}

export interface CalculatedSubline {
    label: string;
    amount: number | null | undefined;
}

export type SaveInput =
    | { mode: 'dollar'; value: number }
    | { mode: 'soft-pct'; percent: number; hardTotal: number }
    | { mode: 'financing-pct'; percent: number }
    | { mode: 'per-rsf'; value: number };

export type CardKey = 'acquisition' | 'hard' | 'soft' | 'financing' | 'operatingReserve';

export interface CardConfig {
    key: CardKey;
    highLevel: IProFormaUses | undefined;
    supplemental?: string;
    editVariant: UsesCardEditVariant;
    editContext?: UsesCardEditContext;
    editableTotal?: number | null;
    editableCostPerRSF?: number | null;
    initialEditMode?: 'dollar' | 'percentage';
    calculatedSublines?: CalculatedSubline[];
}
